import {FC, useEffect, useRef, useState} from 'react'
import {useHistory} from 'react-router-dom'
import {useLayout} from '../../../_metronic/layout/core'
import {defaultProfileImage} from '../../utils/constants'
import {getProfessionalInterests} from '../../services/componentsService'
import {getExperts} from '../../services/expertDirectoryService'
import Pagination from '../components/Pagination'
import {
  AreaOption,
  catalogToAreaOptions,
  deriveAreaOptions,
  Expert,
  formatRate,
  ownerTypeLabel,
} from './expertDirectory'

const LIMIT = 12

// WP-X5 — "Book an Expert" discovery: list available/listed experts with a search + expertise-area
// filter. BLOCKED on the X4 expert-listing read API — a failed fetch shows a "not available yet"
// note instead of crashing.
const BookExpertPage: FC = () => {
  const history = useHistory()
  const {config, setLayout} = useLayout()
  const restoreRef = useRef(config)

  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [area, setArea] = useState('')
  const [page, setPage] = useState(1)
  const [experts, setExperts] = useState<Expert[]>([])
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [areaOptions, setAreaOptions] = useState<AreaOption[]>([])

  const totalPages = count ? Math.ceil(count / LIMIT) : 1

  useEffect(() => {
    restoreRef.current = config
    setLayout({
      ...config,
      header: {...config.header, display: true},
      aside: {...config.aside, display: true},
      toolbar: {...config.toolbar, display: false},
      footer: {...config.footer, display: true},
    })
    return () => setLayout(restoreRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Area filter options from the professional-interests catalog (deployed); fall back to whatever
  // areas the loaded experts expose if that fetch fails.
  useEffect(() => {
    getProfessionalInterests()
      .then((rows: any) => setAreaOptions(catalogToAreaOptions(Array.isArray(rows) ? rows : [])))
      .catch(() => setAreaOptions([]))
  }, [])

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    setPage(1)
  }, [debounced, area])

  useEffect(() => {
    let active = true
    const load = async () => {
      try {
        setLoading(true)
        setError(false)
        const data = await getExperts({
          area: area || undefined,
          search: debounced || undefined,
          limit: LIMIT,
          offset: (page - 1) * LIMIT,
        })
        if (!active) return
        const results: Expert[] = data?.results ?? []
        setExperts(results)
        setCount(Number(data?.count ?? 0))
        // If the catalog fetch yielded nothing, backfill options from the loaded experts.
        setAreaOptions((prev) => (prev.length ? prev : deriveAreaOptions(results)))
      } catch (err) {
        if (!active) return
        setError(true)
        setExperts([])
        setCount(0)
      } finally {
        if (active) setLoading(false)
      }
    }
    load()
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, area, page])

  return (
    <div className='book-expert-page'>
      <div className='d-flex flex-column mb-6'>
        <h1 className='mb-1'>Book an Expert</h1>
        <p className='text-muted'>Engage a member or partner for their expertise.</p>
      </div>

      <div className='d-flex flex-wrap align-items-center gap-3 mb-6'>
        <input
          type='text'
          className='form-control w-auto'
          style={{ flex: '1 1' }}
          placeholder='Search experts'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className='form-select w-auto'
          aria-label='Filter by expertise area'
          style={{ flex: '1 1' }}
          value={area}
          onChange={(e) => setArea(e.target.value)}
          disabled={areaOptions.length === 0}
        >
          <option value=''>All expertise areas</option>
          {areaOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {error ? (
        <div className='text-center py-10 text-muted'>
          The expert directory isn’t available yet. This screen is blocked on the expertise X4
          backend deploy.
        </div>
      ) : !loading && experts.length === 0 ? (
        <div className='text-center py-10'>No experts found.</div>
      ) : (
        <div className='row g-6'>
          {experts.map((e) => (
            <div key={e.id} className='col-12 col-md-6 col-xl-4'>
              <div
                className='card h-100 p-6 cursor-pointer'
                role='button'
                onClick={() => history.push(`/book-expert/${e.id}`)}
              >
                <div className='d-flex align-items-center mb-4'>
                  <img
                    src={defaultProfileImage}
                    alt=''
                    width={56}
                    height={56}
                    className='rounded-circle me-4'
                    style={{objectFit: 'cover'}}
                  />
                  <div>
                    <div className='fw-bold fs-5'>{e.displayName || 'Expert'}</div>
                    {ownerTypeLabel(e.ownerType) && (
                      <span className='badge badge-light-secondary'>
                        {ownerTypeLabel(e.ownerType)}
                      </span>
                    )}
                  </div>
                </div>
                {e.headline && <div className='text-gray-700 mb-3'>{e.headline}</div>}
                <div className='d-flex flex-wrap gap-2 mb-4'>
                  {(e.expertiseAreas ?? []).map((a) => (
                    <span key={a.id} className='badge badge-light-primary'>
                      {a.name}
                    </span>
                  ))}
                </div>
                <div className='mt-auto fw-bold'>
                  {formatRate(e.listedRate, e.currency, e.rateType)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!error && experts.length > 0 && (
        <div className='tap-flex-center mt-8'>
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </div>
  )
}

export default BookExpertPage
